import React, { useRef, useCallback, useEffect, forwardRef } from 'react'
import useToggle from './useToggle'
import usePopper from './usePopper'
import Tooltip from '../components/Tooltip'
import ReactDOM from 'react-dom'

const defaults = { delayIn: 0.5, delayOut: 0 }
export default function useTooltip(ref, toolipRef, inputOptions = {}) {
  const options = { ...defaults, ...inputOptions }
  const [tooltipShowing, toggleTooltip] = useToggle()

  const delayInRef = useRef()
  const delayOutRef = useRef()
  const clearDelays = () => {
    delayInRef.current && clearTimeout(delayInRef.current)
    delayOutRef.current && clearTimeout(delayOutRef.current)
  }
  useEffect(() => clearDelays)

  const showTooltip = useCallback(() => {
    if (options.disabled) return
    clearDelays()
    if (options.delayIn) {
      delayInRef.current = setTimeout(() => {
        toggleTooltip(true)
      }, options.delayIn * 1000)
    } else {
      toggleTooltip(true)
    }
  }, [options.disabled, options.delayIn, toggleTooltip])

  const hideTooltip = useCallback(() => {
    clearDelays()
    if (options.delayOut) {
      delayOutRef.current = setTimeout(() => {
        toggleTooltip(false)
      }, options.delayOut * 1000)
    } else {
      toggleTooltip(false)
    }
  }, [toggleTooltip])

  const { style: tooltipStyle } = usePopper(
    {
      referenceRef: ref,
      popperRef: toolipRef,
      ...options
    },
    [tooltipShowing]
  )

  return [showTooltip, hideTooltip, tooltipShowing, tooltipStyle]
}

const popperDefaults = {
  placement: 'bottom',
  modifiers: { offset: { offset: '0, 10' } }
}

export const withTooltip = Component =>
  forwardRef(({ tooltip, ...props }, ref) => {
    if (!tooltip) return <Component innerRef={ref} {...props} />

    const options = {
      placement: 'bottom',
      modifiers: { offset: { offset: '0, 10' } },
      ...(tooltip.options || {})
    }
    options.modifiers = { ...popperDefaults.modifiers, ...options.modifiers }

    const text = tooltip.text || tooltip

    ref = ref || useRef()
    const tooltipRef = useRef()
    const [showTooltip, hideTooltip, tooltipShowing, tooltipStyle] = useTooltip(
      ref,
      tooltipRef,
      options
    )

    const handleMouseEnter = useCallback(
      e => {
        showTooltip()
        if (props.onMouseEnter) props.onMouseEnter(e)
      },
      [showTooltip, props.onMouseEnter]
    )
    const handleMouseLeave = useCallback(
      e => {
        hideTooltip()
        if (props.onMouseLeave) props.onMouseLeave(e)
      },
      [showTooltip, props.onMouseLeave]
    )
    const handleClick = useCallback(
      e => {
        hideTooltip()
        if (props.onClick) props.onClick(e)
      },
      [showTooltip, props.onClick]
    )
    return (
      <>
        <Component
          {...props}
          innerRef={ref}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={handleClick}
        />
        {tooltipShowing &&
          ReactDOM.createPortal(
            <Tooltip innerRef={tooltipRef} style={tooltipStyle}>
              {text}
            </Tooltip>,
            document.body
          )}
      </>
    )
  })
